import React from 'react';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import {
  QualificationEvaluator,
  SubmissionsOverview,
} from 'components/QualificationEvaluator';
import YearSelectorBar from 'components/YearSelectorBar';
import AccessDenied from 'components/AccessDenied';
import NotAuthenticated from 'components/NotAuthenticated';
import { useQualificationEvaluator } from 'hooks/useQualificationEvaluator';

const QualificationEvaluatorPage: React.FC = () => {
  const {
    isAuthenticated,
    userHasAccess,
    userIsPillarPIC,
    loading,
    year,
    setYear,
    EARLIEST_YEAR,
    currentYear,
    successMessage,
    isRefreshingJudgments,
    viewMode,
    submissions,
    existingJudgments,
    handleSelectSubmission,
    handleCompleteAll,
    isCompletingAll,
    validUserCategories,
    error,
    fetchQualificationSubmissions,
    selectedSubmission,
    handleEvaluationSubmit,
    handleBackToOverview,
    currentSubmissionIndex,
    handleNavigateToSubmission,
  } = useQualificationEvaluator();

  // If not authenticated, show not authenticated message
  if (!isAuthenticated()) {
    return (
      <PageLayout
        title="Qualification Evaluations"
        subtitle="Authentication required to access qualification evaluations."
      >
        <NotAuthenticated
          message="Please log in to access the qualification evaluator. You need to be an authorized category judge to evaluate submissions."
          buttonLink="/"
        />
      </PageLayout>
    );
  }

  // If user doesn't have access, show access denied message
  if (!userHasAccess) {
    return (
      <PageLayout
        title="Qualification Evaluations"
        subtitle="Access restricted to authorized category judges."
      >
        <AccessDenied
          message={
            userIsPillarPIC
              ? 'Pillar PICs do not have access to qualification evaluations. Only category judges and award admins can evaluate qualification submissions.'
              : "You don't have permission to evaluate qualification submissions. Access is restricted to authorized category judges and award admins only."
          }
        />
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout
        title="Qualification Evaluations"
        subtitle="Celebrating excellence across all pillars"
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingState message="Loading qualification submissions..." />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Qualification Evaluations"
      subtitle="In the arena of excellence, may the finest effort claim victory"
    >
      <YearSelectorBar
        year={year}
        setYear={setYear}
        earliestYear={EARLIEST_YEAR}
        currentYear={currentYear}
      />

      {/* Success Message Display */}
      {successMessage && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <p className="text-green-800 font-baijamjuree font-semibold text-sm">
              {successMessage}
            </p>
            {isRefreshingJudgments && (
              <div className="flex items-center gap-2 text-green-600">
                <div className="w-3 h-3 border border-green-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs font-baijamjuree">Updating...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      {viewMode === 'overview' ? (
        <SubmissionsOverview
          submissions={submissions}
          existingJudgments={existingJudgments}
          onSelectSubmission={handleSelectSubmission}
          onCompleteAll={handleCompleteAll}
          isCompletingAll={isCompletingAll}
          userCategory={validUserCategories.join(', ')}
          error={error ?? undefined}
          onRetry={() => fetchQualificationSubmissions(year)}
          validUserCategories={validUserCategories}
        />
      ) : (
        selectedSubmission && (
          <QualificationEvaluator
            key={selectedSubmission.documentId || selectedSubmission.id} // Force re-render on submission change
            submission={selectedSubmission}
            onEvaluationSubmit={handleEvaluationSubmit}
            existingJudgment={
              existingJudgments[
                selectedSubmission.documentId || (selectedSubmission.id as any)
              ]
            }
            onBackToOverview={handleBackToOverview}
            allSubmissions={submissions}
            currentSubmissionIndex={currentSubmissionIndex}
            onNavigateToSubmission={handleNavigateToSubmission}
            isRefreshing={isRefreshingJudgments} // Pass refresh state to prevent flicker
            isLastSubmission={currentSubmissionIndex === submissions.length - 1}
            year={year}
            isReadOnly={year !== currentYear}
          />
        )
      )}
    </PageLayout>
  );
};

export default QualificationEvaluatorPage;
